import React from 'react';
import { Button, Segmented } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon as faIcon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
import { FONT_SIZES } from '../../constants/constants';
import { NavbarButton } from '../Layout/NavbarButton';
import { Checkable } from '../common/Checkable';
import { NavbarListButton } from '../Layout/NavbarListButton';
import DuotoneLightIcon from '../common/DuotoneLightIcon';
import TableStyleSelect from '../Table/TableStyleSelect';
import FontFamilyControl from '../TextStylingComponent/FontFamilyControl';
import FontSizeControl from '../TextStylingComponent/FontSizeControl';
import { filterSameColor, iconButtonStyle, isLightColor, navbarButtonStyle } from '../../utils/styles';
import { cellStyleEntry, ROW_BORDER_SIDES } from '../../shared/tables/tableRowStyle';
import { underlinedPanelHeadingStyle } from '../../shared/ui/panelHeading';
import { getSectionAccentColor } from './BoxFormControlsPortal';
import '../TextStylingComponent/ColorControl.css';

// Libellé de champ et sous-groupe du panel (cf. NavbarTable).
const fieldLabelStyle = {
  fontSize: 12,
  fontWeight: 500,
  color: 'rgba(11, 34, 57, 0.65)',
  whiteSpace: 'nowrap',
};
const subgroupLabelStyle = (color) => ({
  ...underlinedPanelHeadingStyle(color),
  fontSize: 11,
  width: 'fit-content',
});
const noteStyle = { fontSize: 12, color: 'rgba(11, 34, 57, 0.6)' };
const rowStyle = { display: 'flex', alignItems: 'center', gap: 8, width: '100%' };
const choiceStyle = { flex: 1 };

const EMPHASIS = [
  { field: 'bold', titleKey: 'bold', text: 'G', style: { fontWeight: 'bold' } },
  { field: 'italic', titleKey: 'italic', text: 'I', style: { fontStyle: 'italic' } },
  { field: 'underline', titleKey: 'underline', text: 'S', style: { textDecoration: 'underline' } },
];

// Valeurs des colonnes du catalogue (start | center | end), lues de même au rendu.
const ALIGNMENTS = [
  { value: 'start', titleKey: 'left-alignment', icon: 'align-left' },
  { value: 'center', titleKey: 'center-alignment', icon: 'align-center' },
  { value: 'end', titleKey: 'right-alignment', icon: 'align-right' },
];

// Mêmes icônes (et même poids) que le BorderManager des formes et des images.
// Le macro `icon()` se résout à la compilation : une table, pas de nom dynamique.
const BORDER_SIDE_ICONS = {
  all: faIcon({ name: 'border-outer', style: 'light' }),
  top: faIcon({ name: 'border-top', style: 'light' }),
  bottom: faIcon({ name: 'border-bottom', style: 'light' }),
  left: faIcon({ name: 'border-left', style: 'light' }),
  right: faIcon({ name: 'border-right', style: 'light' }),
};

// Infobulle de chaque bouton de couleur. Celui des bordures, comme pour une
// forme : icône de bordure dont le trait prend la couleur choisie.
const TOOLTIP_KEYS = { color: 'font-color', background: 'background-color', borderColor: "border's-color" };

// Même ordre que le BorderManager : tous les côtés, puis gauche, haut, droite, bas.
const BORDER_SIDE_BUTTONS = ['all', 'left', 'top', 'right', 'bottom'];

const isTransparent = (color) => color?.label === 'transparent' || color?.style === 'transparent';

/**
 * Panel de droite pendant la configuration d'un tableau : style du tableau, et
 * style de la ligne cliquée dans l'aperçu du Drawer — pour toutes les lignes
 * de son type ou de son libellé (cf. tableRowStyle).
 * `row` : `{ label, kind }` ou null ; `rule` : sa règle actuelle ; `onChange`
 * reçoit les réglages modifiés, un réglage vide revenant au style du tableau.
 * `column` : libellé d'origine de la colonne de la case cliquée, `scope` ('row' |
 * 'column') la portée des réglages : toute la ligne, ou cette colonne seulement
 * (cf. cellStyleEntry) — où ni les traits ni leur couleur ne se règlent, un trait
 * étant partagé entre deux cases.
 * `row.caption` : légende de la ligne quand elle ne se désigne ni par son type
 * ni par son libellé (ligne d'un tableau custom), `columnName` : nom affiché de
 * la colonne quand `column` n'est qu'une clé.
 * `tableFont` : `{ family, size }` des cases du style du tableau, montrés tant
 * que la ligne n'a pas les siens.
 * `rowsStylable` : faux pour un tableau sans ligne-titre ni total (aperçu non
 * cliquable) : la section « Ligne » n'a alors rien à proposer.
 */
const TableRowStylePanel = ({
  boxId, tableFont, rowsStylable, row, rule: rowRule, column, columnName = column, scope = 'row', onScopeChange, onChange, onReset,
}) => {
  const { t } = useTranslation();
  const colors = useStore(({ configuration }) => configuration.colors);
  const fontFamilies = useStore(({ configuration }) => configuration.fontFamilyList) ?? [];
  const columnScope = scope === 'column' && column != null;
  // En portée « colonne », les boutons montrent ce que la case affiche : ses
  // réglages, à défaut ceux de la ligne.
  const rule = columnScope ? { ...rowRule, ...cellStyleEntry(rowRule, column) } : rowRule;

  const colorButton = (field, icon, { withTransparent, layer = true }) => {
    const list = filterSameColor(colors).filter((color) => withTransparent || !isTransparent(color));
    return (
      <NavbarListButton
        colorInIcon={layer}
        tooltipContent={t(TOOLTIP_KEYS[field])}
        icon={icon}
        listElements={list}
        selectedElement={colors.find((color) => color.style === rule?.[field])}
        // « Transparent » : pas de couleur propre, celle du tableau.
        onListElementClick={(label) => {
          const color = colors.find((c) => c.label === label);
          onChange({ [field]: isTransparent(color) ? null : color?.style });
        }}
      />
    );
  };

  const textColor = rule?.color;
  const colorRows = [
    {
      field: 'color',
      labelKey: 'font-color',
      button: colorButton('color', (
        <p
          className={`font-color-indicator${isLightColor(textColor) ? ' font-color-indicator--outlined' : ''}`}
          style={{ ...iconButtonStyle(), fontFamily: 'monospace', '--font-color-indicator': textColor }}
        >
          A
        </p>
      ), { withTransparent: false }),
    },
    {
      field: 'background',
      labelKey: 'background-color',
      button: colorButton('background', <DuotoneLightIcon name='fill-drip' style={iconButtonStyle()} />, { withTransparent: true }),
    },
    {
      field: 'borderColor',
      labelKey: "border's-color",
      button: colorButton('borderColor', <DuotoneLightIcon name='border-outer' style={iconButtonStyle(true)} />, {
        withTransparent: true,
        layer: 'primary',
      }),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={fieldLabelStyle}>{t('table-style')}</span>
        <TableStyleSelect boxId={boxId} />
      </div>
      {rowsStylable && <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        paddingTop: 12,
        borderTop: '1px solid rgba(11, 34, 57, 0.08)',
      }}>
        <span style={subgroupLabelStyle(getSectionAccentColor(t('table-row-style')))}>{t('table-row-style')}</span>
        {!row ? (
          <span style={noteStyle}>{t('table-row-style-hint')}</span>
        ) : (
          <>
            <span style={noteStyle}>
              {row.caption ?? (row.kind ? t(`table-merge-kind-${row.kind}`) : t('table-label-row-caption', { label: row.label }))}
            </span>
            {column != null && (
              <>
                <Segmented
                  block
                  size='small'
                  value={columnScope ? 'column' : 'row'}
                  onChange={onScopeChange}
                  options={[
                    { value: 'row', label: t('table-row-scope-row') },
                    { value: 'column', label: t('table-row-scope-column') },
                  ]}
                />
                {columnScope && <span style={noteStyle}>{t('table-row-scope-column-note', { column: columnName })}</span>}
              </>
            )}
            <div style={rowStyle}>
              {EMPHASIS.map(({ field, titleKey, text, style }) => (
                <NavbarButton
                  key={field}
                  tooltipContent={t(titleKey)}
                  active={!!rule?.[field]}
                  buttonStyle={choiceStyle}
                  onClick={() => onChange({ [field]: !rule?.[field] })}
                  icon={<p style={{ ...iconButtonStyle(!!rule?.[field]), fontFamily: 'monospace', ...style }}>{text}</p>}
                />
              ))}
            </div>
            <div style={rowStyle}>
              {/* Un nouveau clic sur l'alignement choisi rend celui des colonnes. */}
              {ALIGNMENTS.map(({ value, titleKey, icon }) => (
                <NavbarButton
                  key={value}
                  tooltipContent={t(titleKey)}
                  active={rule?.align === value}
                  buttonStyle={choiceStyle}
                  onClick={() => onChange({ align: rule?.align === value ? null : value })}
                  icon={<DuotoneLightIcon name={icon} weight='solid' style={iconButtonStyle(rule?.align === value)} />}
                />
              ))}
            </div>
            {/* Mêmes contrôles que la section « Texte » ; sans réglage, ils
                montrent la police et la taille des cases du style du tableau. */}
            <div style={rowStyle}>
              {fontFamilies.length > 1 && (
                <FontFamilyControl
                  applyFontFamily={(fontFamily) => onChange({ fontFamily })}
                  fontFamily={rule?.fontFamily ?? tableFont?.family}
                  width={84}
                />
              )}
              <FontSizeControl
                onToggle={(fontSize) => onChange({ fontSize })}
                defaultFontSize={rule?.fontSize ?? tableFont?.size}
                fontSizes={FONT_SIZES}
              />
            </div>
            {colorRows.filter(({ field }) => !columnScope || field !== 'borderColor').map(({ field, labelKey, button }) => (
              <div key={field} style={{ ...rowStyle, justifyContent: 'space-between' }}>
                <span>{t(labelKey)}</span>
                {button}
              </div>
            ))}
            {!columnScope && (
            <>
            {/* Comme les bordures d'une image : un bouton qui déplie les côtés. Un trait
                retiré l'est des deux lignes qui le partagent. */}
            <div style={{ ...rowStyle, justifyContent: 'space-between' }}>
              <span>{t('border')}</span>
              <NavbarListButton
                title={t('border')}
                icon={<FontAwesomeIcon icon={BORDER_SIDE_ICONS.bottom} style={iconButtonStyle()} />}
              >
                {BORDER_SIDE_BUTTONS.map((side) => {
                  const hidden = rule?.hiddenBorders ?? [];
                  const checked = side === 'all' ? !hidden.length : !hidden.includes(side);
                  const next = side === 'all'
                    ? (checked ? [...ROW_BORDER_SIDES] : [])
                    : (checked ? [...hidden, side] : hidden.filter((s) => s !== side));
                  return (
                    <Checkable key={side} checked={checked}>
                      <Button
                        title={t(`table-row-border-${side}`)}
                        style={{ ...navbarButtonStyle, marginBottom: '1px' }}
                        icon={<FontAwesomeIcon icon={BORDER_SIDE_ICONS[side]} style={iconButtonStyle()} />}
                        onMouseDown={() => onChange({ hiddenBorders: next })}
                      />
                    </Checkable>
                  );
                })}
              </NavbarListButton>
            </div>
            </>
            )}
            <Button size='small' block disabled={columnScope ? !cellStyleEntry(rowRule, column) : !rowRule} onClick={onReset}>
              {t('table-row-style-reset')}
            </Button>
          </>
        )}
      </div>}
    </>
  );
};

export default TableRowStylePanel;
